import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Lock, KeyRound, MapPin, Trophy, Send, Loader2, CalendarCheck,
  Sparkles, Users, ImageIcon, BellRing, Award, ShieldCheck, Check,
} from "lucide-react";

/**
 * /private — landing page where a hunter enters the access code.
 * Auth required. Successful redeem → /private/event/:dropId
 */
export function PrivateLandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { code: codeParam } = useParams();
  const [code, setCode] = useState(codeParam || "");
  const [busy, setBusy] = useState(false);
  const [myEvents, setMyEvents] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/private/my-events").then(({ data }) => setMyEvents(data.events || [])).catch(() => {});
  }, [user]);

  const redeem = async (raw) => {
    const c = (raw || code || "").trim();
    if (!c) { toast.error("Enter your access code."); return; }
    if (!user) { toast.error("Please log in first."); navigate("/login?next=/private"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/private/redeem", { code: c });
      toast.success(`You're in${data.event_name ? `: ${data.event_name}` : ""}`);
      navigate(`/private/event/${data.event_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't redeem that code.");
    } finally {
      setBusy(false);
    }
  };

  // Auto-redeem if a code is in the URL and the user is already logged in
  useEffect(() => {
    if (codeParam && user) redeem(codeParam);
    // eslint-disable-next-line
  }, [codeParam, user]);

  if (loading) return <div className="p-10 text-[#F5F2E8]/60">Loading…</div>;

  return (
    <div className="py-10 max-w-2xl mx-auto" data-testid="private-landing">
      <div className="overline">Invite-only</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mt-2">
        Private <span style={{ color: "#D7A62A" }}>hunt</span>
      </h1>
      <p className="text-[#F5F2E8]/70 mt-3">
        Got an access code from a host? Drop it in below — your hunt unlocks instantly. Private events
        never appear on the public map, leaderboard, or Winners Wall.
      </p>

      <div className="loot-card-flat p-6 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} className="text-[#76B82A]" />
          <span className="overline">Access code</span>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SARAH-BDAY"
            className="loot-input flex-1 font-mono tracking-widest text-lg"
            data-testid="private-code-input"
            onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
          />
          <button
            onClick={() => redeem()}
            disabled={busy || !code}
            className="btn-loot inline-flex items-center justify-center gap-2"
            data-testid="private-redeem-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            {busy ? "Unlocking…" : "Unlock hunt"}
          </button>
        </div>
        {!user && (
          <div className="mt-3 text-xs font-mono text-[#F5F2E8]/60">
            Heads up: you'll need to <Link to="/login?next=/private" className="text-[#76B82A] hover:underline">log in</Link> or <Link to="/register?next=/private" className="text-[#76B82A] hover:underline">create an account</Link> before unlocking.
          </div>
        )}
      </div>

      {myEvents.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-2">Your private events</div>
          <div className="space-y-2">
            {myEvents.map((ev) => (
              <Link
                key={ev.event_id}
                to={`/private/event/${ev.event_id}`}
                className="loot-card-flat p-4 block hover:border-[#76B82A] transition-colors"
                data-testid={`private-my-event-${ev.event_id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-lg">{ev.event_name || "Private event"}</div>
                    <div className="font-mono text-xs text-[#F5F2E8]/60 mt-0.5">
                      {ev.client_name && <>{ev.client_name} · </>}
                      {ev.city} · joined {new Date(ev.joined_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm"
                    style={{
                      background: ev.status === "live" ? "rgba(118,184,42,0.15)" : "rgba(215,166,42,0.15)",
                      color: ev.status === "live" ? "#76B82A" : "#D7A62A",
                    }}
                  >
                    {ev.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * /private/event/:eventId — event view + every drop in the event.
 */
export function PrivateEventPage() {
  const { dropId: eventId } = useParams(); // route param historically named dropId; semantics now event id
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [drops, setDrops] = useState([]);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=/private/event/${eventId}`); return; }
    (async () => {
      try {
        const { data } = await api.get(`/private/events/${eventId}`);
        setEvent(data.event);
        setDrops(data.drops || []);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Couldn't load this hunt.");
      }
      try {
        const { data: b } = await api.get(`/private/events/${eventId}/leaderboard`);
        setBoard(b);
      } catch {/* ignore */}
    })();
  }, [authLoading, user, eventId, navigate]);

  if (authLoading || (!event && !error)) {
    return <div className="py-10 text-[#F5F2E8]/60">Loading private hunt…</div>;
  }
  if (error) {
    return (
      <div className="py-10 max-w-xl mx-auto">
        <div className="loot-card-flat p-8 text-center">
          <Lock className="mx-auto mb-3 text-[#F5F2E8]/40" size={32} />
          <h2 className="font-display uppercase text-2xl">Locked</h2>
          <p className="text-[#F5F2E8]/70 mt-2">{error}</p>
          <Link to="/private" className="btn-loot inline-flex mt-4">Enter access code</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 space-y-6 max-w-3xl mx-auto" data-testid="private-event-page">
      {event.custom_card_url && (
        <img
          src={event.custom_card_url}
          alt={event.name}
          className="w-full max-w-md mx-auto block border border-[#F5F2E8]/15"
          data-testid="private-custom-card"
        />
      )}
      <div>
        <div className="overline">Private event</div>
        <h1 className="font-display uppercase text-4xl tracking-tight mt-1">
          {event.name} <span style={{ color: "#D7A62A" }}>·</span>{" "}
          <span style={{ color: "#76B82A" }}>{event.city}</span>
        </h1>
        {event.client_name && (
          <div className="font-mono text-xs text-[#F5F2E8]/60 mt-1">
            Hosted by {event.client_name}
          </div>
        )}
      </div>

      <div className="loot-card-flat p-5">
        <div className="overline mb-2">Status · {drops.length} drop{drops.length === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-3">
          <span
            className="font-display text-2xl"
            style={{ color: event.status === "live" ? "#76B82A" : "#D7A62A" }}
          >
            {event.status?.toUpperCase()}
          </span>
        </div>
      </div>

      {drops.length === 0 ? (
        <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">
          No drops in this event yet — your host hasn't added them.
        </div>
      ) : (
        drops.map((d, idx) => (
          <div key={d.id} className="loot-card-flat p-5 space-y-3" data-testid={`event-drop-${d.id}`}>
            <div>
              <div className="overline" style={{ color: "#D7A62A" }}>Drop {idx + 1}</div>
              <h3 className="font-display uppercase text-2xl mt-1">{d.title}</h3>
              <div className="font-mono text-xs text-[#F5F2E8]/60 mt-1">
                Status: <span style={{ color: d.status === "live" ? "#76B82A" : "#D7A62A" }}>{d.status?.toUpperCase()}</span>
                {d.launch_time && <> · started {new Date(d.launch_time).toLocaleString()}</>}
              </div>
            </div>
            {(d.zone_description || d.zone) && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin size={14} className="mt-0.5 text-[#76B82A]" />
                <span>{d.zone_description || "Defined zone"}</span>
              </div>
            )}
            <div>
              <div className="overline mb-2">Clues</div>
              <div className="space-y-2">
                {(d.clues || []).length === 0 && (
                  <div className="text-[#F5F2E8]/40 text-sm">No clues yet.</div>
                )}
                {(d.clues || []).map((c, i) => (
                  <div key={i} className={`p-3 border ${c.unlocked ? "border-[#76B82A]/30" : "border-[#F5F2E8]/10 clue-locked"}`}>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A] mb-1">
                      Clue {c.order} · {c.type || "clue"}
                      {!c.unlocked && c.release_offset_minutes ? ` · unlocks at +${c.release_offset_minutes}m` : ""}
                    </div>
                    <div>{c.unlocked ? c.text : "🔒 Locked — wait for release"}</div>
                  </div>
                ))}
              </div>
            </div>
            <Link
              to={`/drops/${d.id}`}
              className="btn-gold inline-flex items-center gap-2 text-xs"
              data-testid={`event-drop-claim-${d.id}`}
            >
              <Send size={12} /> Submit a find
            </Link>
          </div>
        ))
      )}

      {board && (
        <div className="loot-card-flat p-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} className="text-[#D7A62A]" />
            <span className="overline">Private leaderboard</span>
            <span className="font-mono text-[10px] text-[#F5F2E8]/50 ml-auto">
              {board.hunters_seated} hunter{board.hunters_seated === 1 ? "" : "s"} in this event
            </span>
          </div>
          {board.leaderboard.length === 0 ? (
            <div className="text-[#F5F2E8]/40 text-sm flex items-center gap-2">
              <CalendarCheck size={14} /> Be the first to find one.
            </div>
          ) : (
            <ol className="space-y-1 text-sm">
              {board.leaderboard.map((r) => (
                <li key={`${r.user_id}-${r.drop_id}`} className="flex justify-between gap-3">
                  <span>
                    <span className="font-mono text-[#D7A62A] mr-2">#{r.rank}</span>
                    @{r.username || "—"}
                  </span>
                  <span className="font-mono text-[#F5F2E8]/60">
                    {r.time_to_find_minutes ? `${r.time_to_find_minutes} min` : "—"}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}



/* ----------------------------------------------------------------- SalesPage */

const ICON_MAP = {
  Users, ImageIcon, BellRing, ShieldCheck, KeyRound, Sparkles, Award, Lock,
  CalendarCheck, Mail: Send, // safe fallback if admin uses unknown icon
};
function pickIcon(name) {
  return ICON_MAP[name] || Sparkles;
}

function PriceCard({ pkg, onBook, busy, highlight }) {
  if (!pkg) return null;
  return (
    <div
      className="loot-card-flat p-6 flex flex-col"
      style={highlight ? { borderColor: "#D7A62A" } : {}}
      data-testid={`price-card-${pkg.id}`}
    >
      <div className="overline" style={{ color: highlight ? "#D7A62A" : "#76B82A" }}>
        {highlight ? "Most popular" : "Starter tier"}
      </div>
      <h3 className="font-display uppercase text-3xl mt-1">{pkg.label}</h3>
      <p className="text-[#F5F2E8]/70 mt-2 text-sm">{pkg.description}</p>
      <div className="flex items-end gap-1 mt-5">
        {pkg.promo_active && (
          <span className="font-mono text-sm line-through text-[#F5F2E8]/40 mr-2">
            ${pkg.amount.toFixed(0)}
          </span>
        )}
        <span className="font-display text-5xl" style={{ color: highlight ? "#D7A62A" : "#76B82A" }}>
          ${(pkg.effective_amount ?? pkg.amount).toFixed(0)}
        </span>
        <span className="font-mono text-xs text-[#F5F2E8]/60 mb-2">USD · one-time</span>
      </div>
      {pkg.promo_active && pkg.promo_label && (
        <div className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "#E53935" }}>
          {pkg.promo_label}
        </div>
      )}
      <ul className="mt-4 space-y-1.5 flex-1">
        {(pkg.perks || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#76B82A" }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => onBook(pkg.id)}
        disabled={busy}
        className={`${highlight ? "btn-gold" : "btn-loot"} mt-5 inline-flex items-center justify-center gap-2`}
        data-testid={`book-${pkg.id}-btn`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {busy ? "Opening checkout…" : "Book a Hunt"}
      </button>
    </div>
  );
}

export function PrivateSalesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    city: "", website: "", description: "",
  });
  const [showForm, setShowForm] = useState(null); // package_id

  useEffect(() => {
    api.get("/payments/packages")
      .then(({ data }) => setPackages(data.packages || []))
      .catch(() => setPackages([]));
    api.get("/private/sales-settings")
      .then(({ data }) => setSettings(data))
      .catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        contact_name: f.contact_name || user.name || "",
        email: f.email || user.email || "",
        city: f.city || user.city || "",
      }));
    }
  }, [user]);

  const privatePkg = packages.find((p) => p.id === "private_event");
  const corporatePkg = packages.find((p) => p.id === "corporate_hunt");

  const handleBookClick = (pkgId) => {
    if (!user) {
      navigate(`/login?next=/private/sales`);
      return;
    }
    setShowForm(pkgId);
  };

  const submit = async (pkgId) => {
    if (!form.contact_name || !form.email || !form.city) {
      toast.error("Name, email, and city are required.");
      return;
    }
    setBusy(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        business_name: form.business_name || form.contact_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        website: form.website,
        description: `[Private Hunt booking] ${form.description || ""}`.slice(0, 400),
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't open checkout. Try again or email us.");
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Checkout failed.";
      const supportEmail = settings?.support_email;
      toast.error(
        <span>
          {msg}{" "}
          {supportEmail && (
            <>
              <a
                href={`mailto:${supportEmail}?subject=Private%20Hunt%20booking%20—%20${pkgId}`}
                className="underline text-[#76B82A]"
              >
                Email us
              </a>{" "}
              and we'll handle it manually.
            </>
          )}
        </span>,
        { duration: 8000 },
      );
    } finally {
      setBusy(null);
    }
  };

  if (!settings) return <div className="py-10 text-[#F5F2E8]/60">Loading…</div>;

  const supportEmail = settings.support_email;
  const supportLabel = settings.support_email_label || "Email us";
  const steps = settings.how_steps || [];
  const features = settings.compare_features || [];
  const testimonials = settings.testimonials || [];

  return (
    <div className="py-10 max-w-6xl mx-auto space-y-12" data-testid="private-sales-page">
      {/* Hero */}
      <section>
        <div className="overline" style={{ color: "#D7A62A" }}>{settings.hero_overline}</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-2 leading-[0.95]">
          {settings.hero_headline_part1}{" "}
          <span style={{ color: "#D7A62A" }}>{settings.hero_headline_part2}</span><br />
          {settings.hero_headline_part3}{" "}
          <span style={{ color: "#76B82A" }}>{settings.hero_headline_part4}</span>
        </h1>
        <p className="text-[#F5F2E8]/75 mt-5 max-w-2xl text-lg whitespace-pre-line">
          {settings.hero_subhead}
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <a
            href="#pricing"
            className="btn-gold inline-flex items-center gap-2"
            data-testid="sales-jump-pricing"
          >
            {settings.primary_cta_label}
          </a>
          <Link to="/private" className="btn-ghost inline-flex items-center gap-2">
            {settings.secondary_cta_label}
          </Link>
        </div>
      </section>

      {/* How it works */}
      {steps.length > 0 && (
        <section>
          <div className="overline mb-3">{settings.how_overline}</div>
          <div className="grid md:grid-cols-3 gap-4">
            {steps.map((s, i) => (
              <div key={i} className="loot-card-flat p-5">
                <div className="font-display text-3xl" style={{ color: "#76B82A" }}>{s.n}</div>
                <h4 className="font-display uppercase text-xl mt-1">{s.t}</h4>
                <p className="text-[#F5F2E8]/70 text-sm mt-2 whitespace-pre-line">{s.b}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pricing */}
      <section id="pricing" className="scroll-mt-20">
        <div className="overline mb-3">{settings.pricing_overline}</div>
        <h2 className="font-display uppercase text-4xl mb-6">
          {settings.pricing_headline_part1}{" "}
          <span style={{ color: "#D7A62A" }}>{settings.pricing_headline_part2}</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <PriceCard pkg={privatePkg} onBook={handleBookClick} busy={busy === "private_event"} />
          <PriceCard pkg={corporatePkg} onBook={handleBookClick} busy={busy === "corporate_hunt"} highlight />
        </div>
        <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-3">
          {settings.pricing_footnote}{" "}
          <a className="text-[#76B82A] hover:underline" href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </div>
      </section>

      {/* Feature comparison */}
      {features.length > 0 && (
        <section>
          <div className="overline mb-3">{settings.compare_overline}</div>
          <div className="loot-card-flat overflow-x-auto">
            <table className="w-full text-sm" data-testid="feature-compare-table">
              <thead className="bg-[#F5F2E8]/5">
                <tr>
                  <th className="px-4 py-3 text-left">Feature</th>
                  <th className="px-4 py-3 text-center font-display uppercase">{privatePkg?.label || "Private Event"}</th>
                  <th className="px-4 py-3 text-center font-display uppercase" style={{ color: "#D7A62A" }}>{corporatePkg?.label || "Corporate Hunt"}</th>
                </tr>
              </thead>
              <tbody>
                {features.map((row, i) => {
                  const Icon = pickIcon(row.icon);
                  const cell = (v) =>
                    v === true ? <Check size={16} className="inline" style={{ color: "#76B82A" }} />
                      : v === false ? <span className="text-[#F5F2E8]/30">—</span>
                      : <span className="font-mono">{v}</span>;
                  return (
                    <tr key={i} className="border-t border-[#F5F2E8]/10">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <Icon size={14} className="text-[#76B82A]" /> {row.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{cell(row.private_event)}</td>
                      <td className="px-4 py-3 text-center">{cell(row.corporate_hunt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Social proof */}
      {testimonials.length > 0 && (
        <section>
          <div className="overline mb-3">{settings.testimonials_overline}</div>
          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div key={i} className="loot-card-flat p-5">
                <div className="font-display text-4xl" style={{ color: "#D7A62A" }}>"</div>
                <p className="text-[#F5F2E8]/85 mt-1 whitespace-pre-line">{t.quote}</p>
                <div className="font-mono text-xs text-[#F5F2E8]/60 mt-3">{t.who}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Direct contact CTA */}
      <section className="loot-card-flat p-6 text-center">
        <div className="overline mb-2">Questions?</div>
        <p className="text-[#F5F2E8]/85">
          Email <a className="text-[#76B82A] hover:underline" href={`mailto:${supportEmail}`}>{supportEmail}</a>{" "}
          or use the <Link to="/contact" className="text-[#76B82A] hover:underline">contact form</Link>.
        </p>
      </section>

      {/* Booking modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setShowForm(null)}
        >
          <div
            className="loot-card-flat p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-form"
          >
            <div className="overline mb-2">
              {showForm === "corporate_hunt" ? (corporatePkg?.label || "Corporate Hunt") : (privatePkg?.label || "Private Event")} · book
            </div>
            <h3 className="font-display uppercase text-2xl mb-4">Tell us the basics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="loot-input md:col-span-2" placeholder="Your name *" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="book-name" />
              <input className="loot-input" placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" />
              <input className="loot-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input className="loot-input" placeholder="Company / family *" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="book-company" />
              <input className="loot-input" placeholder="City *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="book-city" />
              <input className="loot-input md:col-span-2" placeholder="Website (optional)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              <textarea className="loot-input md:col-span-2" rows={3} placeholder="Tell us about the event — date, theme, group size" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-desc" />
            </div>
            <div className="flex flex-wrap gap-3 mt-5 justify-end">
              <button onClick={() => setShowForm(null)} className="btn-ghost text-xs">Cancel</button>
              <button
                onClick={() => submit(showForm)}
                disabled={busy === showForm}
                className="btn-gold inline-flex items-center gap-2"
                data-testid="book-submit"
              >
                {busy === showForm ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {busy === showForm ? "Opening checkout…" : "Continue to Stripe"}
              </button>
            </div>
            <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-3">
              You'll review the price on Stripe before paying. We confirm event details by email afterward.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
